import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import {
  EMPTY_KARTY_UPDATE,
  type KartyGameUpdate,
  type KartySession,
} from "@ziolo/shared";

export function useKartySocket(enabled: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const [table, setTable] = useState<KartyGameUpdate>(EMPTY_KARTY_UPDATE);
  const [session, setSession] = useState<KartySession | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const socket = io(`${window.location.origin}/karty`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć ze stołem. Odśwież stronę.",
      );
    });
    socket.on("game_update", (update: KartyGameUpdate) => setTable(update));
    socket.on("session", (next: KartySession) => setSession(next));
    socket.on("notice", (payload: { text: string }) => {
      setNotice(payload.text);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setSession(null);
      setTable(EMPTY_KARTY_UPDATE);
    };
  }, [enabled]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const playCard = useCallback((card: string) => {
    socketRef.current?.emit("play_card", { card });
  }, []);

  const chooseWinner = useCallback((userId: number) => {
    socketRef.current?.emit("choose_winner", { userId });
  }, []);

  return { table, session, connected, error, notice, playCard, chooseWinner };
}
